import { FilterGroup } from './model.filterGroup'

export const reportTypes = [
  'null',
  'completion',
  'playthroughs',
  'knowledge',
  'activity',
  'playtime',
  'repetitions',
  'users',
  'surveys',
  'moduleCompletion',
  'productCompletion',
  'categoryCompletion',
  'eventAttendance',
  'certificationStatus',
  'productList',
  'realTalkUserTranscript',
  'moduleLevelLearnerAnalytics',
  'testError',
] as const

export const timeFrameTypes = [
  null,
  'last_7_days',
  'last_30_days',
  'last_12_months',
  'all_time',
  'custom',
] as const

export const activeStatus = [null, 'all', 'active', 'inactive'] as const
export const reportStatus = [null, 'all', 'reports_on', 'reports_off'] as const
export const dataFields = [
  'certified_date',
  'stars',
  'high_score',
  'times_played',
] as const
export const certificationStatus = ['Certified', 'To Do', 'Overdue'] as const

export const calculatedFieldTypes = [
  'average_stars',
  'total_score',
  'percentage_certified',
  'first_play_date',
  'last_play_date',
  'times_played',
  'playtime_seconds',
] as const

export const userDetailsField = [
  'user_id',
  'external_user_id',
  'name',
  'email',
  'phone_number',
  'position_name',
  'additional_positions',
  'department_name',
  'additional_departments',
  'custom_groups',
  'anonymous',
  'created_at',
  'started_at',
  'global_region',
  'country',
] as const

export type ReportType = typeof reportTypes[number]
export type TimeFrameType = typeof timeFrameTypes[number]
export type ActiveStatus = typeof activeStatus[number]
export type ReportStatus = typeof reportStatus[number]
export type DataField = typeof dataFields[number]
export type CalculatedFieldType = typeof calculatedFieldTypes[number]
export type UserDetailsField = typeof userDetailsField[number]
export type CertificationStatusType = typeof certificationStatus[number]

export type eventInstancesLastDate = {
  amount: number
  type: string
}

export type eventInstancesFutureDate = eventInstancesLastDate

export interface ReportLock {
  isLocked: boolean
  lockedBy: number | null // user id who locked the report (matches reports.owner_id semantics)
  lockedAt: string | null // ISO timestamp
}

// Default lock state for a report that has never been locked. Returns a fresh
// object each call so callers can't accidentally share/mutate one instance.
export const unlockedLock = (): ReportLock => ({
  isLocked: false,
  lockedBy: null,
  lockedAt: null,
})

export interface Report {
  id: string
  ownerId?: number
  updatedBy?: number // user id who wrote the latest version (reports.updated_by)
  updatedAt?: Date // when the latest version was written (reports.version_created_at)
  lock?: ReportLock
  title: string
  description: string
  type: ReportType
  timeframe_type: TimeFrameType
  timeframe_from: string
  timeframe_to: string
  activeStatus: ActiveStatus
  reportStatus: ReportStatus
  created?: Date
  selectedProducts: FilterGroup[]
  selectedDepartments: FilterGroup[]
  selectedPositions: FilterGroup[]
  selectedCustomGroups: FilterGroup[]
  selectedModules: FilterGroup[]
  selectedModuleCategories: FilterGroup[]
  selectedUsers: FilterGroup[]
  selectedInstructors: FilterGroup[]
  selectedEventInstances: FilterGroup[]
  portalCategories: FilterGroup[]
  selectedFields: string[]
  formatedReport: boolean | null
  dynamicReport: boolean | null
  dynamicUserLevelReport: boolean | null
  includeChildrenDepartments: boolean | null
  userDetails: UserDetailsField[] | null
  calculatedFields: CalculatedFieldType[] | null
  dataField: DataField | null
  orderByField: string | null
  orderByDirection: string | null
  attended: boolean | null
  mandatory: boolean | null
  language: string | null
  eventInstancesLastDate?: eventInstancesLastDate
  eventInstancesFutureDate?: eventInstancesFutureDate
  selectedHomePageDepartments: FilterGroup[]
  isEventInstanceSelected?: boolean
  certificationStatus: CertificationStatusType[] | null
  departmentLevelsCompletion: number[] | null
  draft: boolean | null
}

export interface ReportConfig {
  type: ReportType
  timeframe_type: TimeFrameType
  timeframe_from: string
  timeframe_to: string
  activeStatus: ActiveStatus
  reportStatus: ReportStatus
  selectedProducts: FilterGroup[]
  selectedDepartments: FilterGroup[]
  selectedPositions: FilterGroup[]
  selectedCustomGroups: FilterGroup[]
  selectedModules: FilterGroup[]
  selectedModuleCategories: FilterGroup[]
  selectedUsers: FilterGroup[]
  selectedInstructors: FilterGroup[]
  selectedEventInstances: FilterGroup[]
  portalCategories: FilterGroup[]
  selectedFields: string[]
  userDetails: UserDetailsField[] | null
  calculatedFields: CalculatedFieldType[] | null
  formatedReport: boolean | null
  dynamicReport: boolean | null
  dynamicUserLevelReport: boolean | null
  includeChildrenDepartments: boolean | null
  dataField: DataField | null
  orderByField: string | null
  orderByDirection: string | null
  attended: boolean | null
  mandatory: boolean | null
  language: string | null
  eventInstancesLastDate?: eventInstancesLastDate
  eventInstancesFutureDate?: eventInstancesFutureDate
  selectedHomePageDepartments: FilterGroup[]
  isEventInstanceSelected?: boolean
  certificationStatus: CertificationStatusType[] | null
  departmentLevelsCompletion: number[] | null
  lock?: ReportLock
}
